import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { type User, type LoginResponse, UserRole, Permission, ROLE_PERMISSIONS, normalizeRoleKey } from '../types/auth';

export interface AuthContextType {
  user: User | null;
  jwtToken: string | null;
  isLoggedIn: boolean;
  loading: boolean;
  checkingAuth: boolean;
  
  // ✅ NUEVOS: Métodos para permisos
  userRoles: UserRole[];
  hasRole: (role: UserRole) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string; user?: User }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  return context;
};

// ✅ AGREGADO: Variable de entorno para la URL base
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = sessionStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [jwtToken, setJwtToken] = useState<string | null>(() => sessionStorage.getItem('jwtToken'));
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !!sessionStorage.getItem('jwtToken'));
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);

  // Comprobación inicial una sola vez
  useEffect(() => {
    setCheckingAuth(false);
  }, []);

  // Guarda sesión al cambiar
  useEffect(() => {
    if (isLoggedIn && user && jwtToken) {
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('jwtToken', jwtToken);
    }
  }, [isLoggedIn, user, jwtToken]);

  // Limpia storage al salir
  useEffect(() => {
    if (!isLoggedIn) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('jwtToken');
    }
  }, [isLoggedIn]);

  // Mantiene la presencia mientras la sesión sigue abierta, aunque la pantalla esté en reposo.
  useEffect(() => {
    if (!isLoggedIn || !jwtToken) return;

    const ping = () => {
      fetch(`${API_BASE_URL}/api/auth/presence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwtToken}` },
        keepalive: true
      }).catch(() => {});
    };

    ping();
    const id = window.setInterval(ping, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoggedIn, jwtToken]);

  // Roles canónicos (el backend puede enviar ROLE_XXX, guiones o mayúsculas distintas)
  const userRoles: UserRole[] = useMemo(() => {
    if (!user?.roles?.length) return [];
    const seen = new Set<UserRole>();
    const out: UserRole[] = [];
    for (const r of user.roles) {
      const k = normalizeRoleKey(r);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }, [user?.roles]);

  // ✅ NUEVO: Verificar si el usuario tiene un rol específico
  const hasRole = (role: UserRole): boolean => {
    return userRoles.includes(role);
  };

  // ✅ NUEVO: Verificar si el usuario tiene alguno de los roles
  const hasAnyRole = (roles: UserRole[]): boolean => {
    return roles.some(role => userRoles.includes(role));
  };

  // ✅ NUEVO: Verificar si el usuario tiene un permiso específico
  const hasPermission = (permission: Permission): boolean => {
    return userRoles.some(role => ROLE_PERMISSIONS[role]?.includes(permission));
  };

  // ✅ NUEVO: Verificar si es administrador
  const isAdmin = (): boolean => {
    return hasRole(UserRole.ADMIN);
  };

  const login = async (username: string, password: string) => {
    setLoading(true);
    try {
      // ✅ CAMBIADO: URL dinámica usando variable de entorno
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (res.ok) {
        const data: LoginResponse = await res.json();
        setJwtToken(data.token);
        setUser(data.user); // ✅ ACTUALIZADO: Ahora incluye roles
        setIsLoggedIn(true);
        return { success: true, user: data.user };
      } else {
        return { success: false, message: 'Usuario o contraseña incorrectos' };
      }
    } catch (err: any) {
      return { success: false, message: err.message || 'Error de red' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    const token = jwtToken || sessionStorage.getItem('jwtToken');
    if (token) {
      fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true
      }).catch(() => {});
    }
    setUser(null);
    setJwtToken(null);
    setIsLoggedIn(false);
    sessionStorage.clear();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      jwtToken, 
      isLoggedIn, 
      loading, 
      checkingAuth, 
      userRoles,
      hasRole,
      hasPermission,
      hasAnyRole,
      isAdmin,
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;